<template>
    <div>
      <div class="dashboard-page-title">
        <div class="container-fluid">
          <h1>User Import</h1>
          <div class="spacer-b-2 w-25">
            <label for="role">Select import type</label>
            <selectize
              id="role"
              class="selectize selectize-single"
              name="role"
              v-model="role"
              placeholder="Role"
              :options="roleOptions"/>
          </div>
          <p v-if="role !== 'profile_images'">Download a CSV and bulk import. <a @click="downloadExample(operations.create, $event)" href="#" target="_blank">Download an example file</a>. <br/> Optionally add profile images, compressed in a zip file.</p>
          <p v-else>Download a CSV and bulk profile image import. <a @click="downloadExample(operations.update_profile_images, $event)" href="#" target="_blank">Download an example file</a>. <br/> Add profile images, compressed in a zip file.</p>
          <p>Expected date format : YYYY-MM-DD (e.g. 1990-12-31)<br/>
          Expected value separator : , (e.g. Alabama, Alaska, Arizona)</p>
        </div>
      </div>
      <div class="container-fluid">
        <div class="dashboard-block">
          <form>
            <div class="panel panel-grey" :class="{ 'is-droppable' : dropable }">
              <div class="panel-body">
                <div class="form-horizontal">
                  <div class="row">
                    <div class="col-md-12 text-center" v-if="isSuccess">
                      <h3>{{ selectedRoleLabel | sentenceCase }} file uploaded. Users will be reviewed and added/updated to your
                        <router-link :to="'/' + selectedRoleLink">list</router-link>.
                      </h3>
                    </div>
                    <div class="col-md-12">
                      <div class="dropbox" @drop="fileDropped" @dragenter="dropable = true" @dragleave="dropable = false">
                        <input
                          v-if="isInitial"
                          name="uploadFile"
                          :disabled="isSaving"
                          type="file"
                          @change="setFiles($event.target.files)"
                          class="form-control input-file"
                          accept="text/csv,zip,application/octet-stream,application/zip,application/x-zip,application/x-zip-compressed" />
                        <help-block field="file"/>
                        <p v-if="isInitial">
                          Drag your file(s) here to begin <br />
                          or click to browse
                        </p>
                        <p v-if="isSuccess">
                          User File Uploaded <br />
                          Drag your file(s) here
                        </p>
                        <p v-if="isSaving">
                          Uploading files...
                        </p>
                        <p v-if="isFailedWithErrorsAndWarnings">
                          Failed to import
                        </p>
                        <span v-if="this.uploadFile && isInitial">Upload File: {{this.uploadFile.name}}</span>
                        <span v-if="this.profileImages && isInitial">Profile images (zip) File: {{this.profileImages.name}}</span>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12" v-if="isFailed">
                      <validate-input :file-error="fileError" :importer-errors="importerErrors" :importer-warnings="importerWarnings"></validate-input>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div class="dashboard-page-title">
        <div class="container-fluid">
          <button
            v-if="isInitial"
            type="button"
            id="formSubmit"
            name="formSubmit"
            class="btn btn-primary"
            @click="uploadFiles(false)"
          >Start upload</button>
          <button
            v-if="isSuccess"
            type="button"
            id="formSubmit"
            name="formSubmit"
            class="btn btn-primary"
            @click="reset"
          >Upload another file</button>
          <button
            v-if="isFailed"
            type="button"
            class="btn btn-primary"
            @click="reset"
          >Retry</button>
          <button
            v-if="isFailedWithWarningsOnly"
            type="button"
            id="overrideAndUpload"
            name="overrideAndUpload"
            class="btn btn-primary"
            @click="uploadFiles(true)"
          >Ignore warnings and upload</button>
        </div>
      </div>
    </div>
</template>

<script>
import ValidateInput from './ValidateInput'
import Selectize from '../ext/codium/CodiumSelectize'
import { notify } from 'utils'
const STATUS_INITIAL = 0
const STATUS_SAVING = 1
const STATUS_SUCCESS = 2
const STATUS_FAILED_WITH_ERRORS_AND_WARNINGS = 3
const STATUS_FAILED_WITH_ERRORS_ONLY = 4
const STATUS_FAILED_WITH_WARNINGS_ONLY = 5

export default {
  name: 'Importer',
  components: {
    ValidateInput,
    Selectize
  },
  data () {
    return {
      operations: {
        create: 1,
        update: 2,
        update_profile_images: 3
      },
      uploadedFiles: [],
      uploadError: null,
      currentStatus: null,
      importerErrors: [],
      importerWarnings: [],
      fileError: null,
      role: 'talent',
      uploadFile: null,
      profileImages: null,
      dropable: false
    }
  },
  computed: {
    roleOptions () {
      const options = [
        { id: 'talent', text: this.$options.filters.sentenceCase(this.$starLabel), link: 'talent' }
      ]
      if (this.$userIsAdmin) {
        options.push({ id: 'client', text: 'Client', link: 'clients' })
      }
      if (this.$managerModuleEnabled) {
        options.push({ id: 'manager', text: 'Manager', link: 'managers' })
      }
      if (this.$userIsSuperAdmin) {
        options.push({ id: 'admin', text: 'Admin', link: 'system/admin-management' })
        options.push({ id: 'profile_images', text: 'Profile Images for existing users', link: 'system/admin-management' })
      }
      return options
    },
    selectedRoleLabel () {
      return this.roleOptions.find(element => element.id === this.role).text
    },
    selectedRoleLink () {
      return this.roleOptions.find(element => element.id === this.role).link
    },
    isInitial () {
      return this.currentStatus === STATUS_INITIAL
    },
    isSaving () {
      return this.currentStatus === STATUS_SAVING
    },
    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },
    isFailedWithErrorsAndWarnings () {
      return this.currentStatus === STATUS_FAILED_WITH_ERRORS_AND_WARNINGS
    },
    isFailedWithErrorsOnly () {
      return this.currentStatus === STATUS_FAILED_WITH_ERRORS_ONLY
    },
    isFailedWithWarningsOnly () {
      return this.currentStatus === STATUS_FAILED_WITH_WARNINGS_ONLY
    },
    isFailed () {
      return this.isFailedWithErrorsAndWarnings || this.isFailedWithWarningsOnly || this.isFailedWithErrorsOnly
    }
  },
  methods: {
    reset () {
      // reset form to initial state
      this.currentStatus = STATUS_INITIAL
      this.uploadedFiles = []
      this.uploadFile = null
      this.profileImages = null
      this.uploadError = null
      this.importerErrors = []
      this.importerWarnings = []
      this.fileError = null
    },
    save (formData) {
      // upload data to the server
      this.currentStatus = STATUS_SAVING
      this.$http.post(`user-import/${this.role}`, formData)
        .then((response) => {
          this.uploadedFiles = [].concat(response)
          this.currentStatus = STATUS_SUCCESS
          notify.success('Your file has been uploaded and will be processed shortly')
          this.importerErrors = []
          this.fileError = null
        })
        .catch(err => {
          notify.error('There are issues with your import. Please review and try again')

          if (err.response.status === 422) {
            this.updateStatus(err.response)
            if (err.response.data.errors) {
              this.importerErrors = err.response.data.errors
            }
            if (err.response.data.warnings) {
              this.importerWarnings = err.response.data.warnings
            }
            this.uploadError = err.response
          } else if (err.response.data.message) {
            this.fileError = err.response.data.message
          }
        })
    },
    updateStatus (response) {
      if (response.data.errors && response.data.warnings) {
        this.currentStatus = STATUS_FAILED_WITH_ERRORS_AND_WARNINGS
      } else if (response.data.errors) {
        this.currentStatus = STATUS_FAILED_WITH_ERRORS_ONLY
      } else if (response.data.warnings) {
        this.currentStatus = STATUS_FAILED_WITH_WARNINGS_ONLY
      } else {
        this.currentStatus = STATUS_FAILED_WITH_ERRORS_AND_WARNINGS
      }
    },
    fileDropped (e) {
      this.dropable = false

      if (!e.dataTransfer.files || !e.dataTransfer.files.length) {
        return
      }

      this.setFiles(e.dataTransfer.files)
    },
    setFiles (files) {
      if (files.length !== 0) {
        for (let i = 0; i < files.length; i++) {
          if (files[i].size >= 30000000) {
            notify.error('Maximum file size is 30MB. Unable to upload file: ' + files[i].name)
          } else {
            // Second upload will get override. Only file file from certain type is accepted
            if (files[i].type === 'text/csv' || files[i].type === 'application/vnd.ms-excel') {
              this.uploadFile = files[i]
            } else if (files[i].type.includes('zip')) {
              this.profileImages = files[i]
            } else {
              notify.error('Only .zip or .csv allowed')
            }
          }
        }
      }
    },
    uploadFiles (ignoreWarnings) {
      // handle file changes
      const formData = new FormData()

      // if option is profile images, set the operation
      if (this.role === 'profile_images') {
        formData.set('operation', this.operations.update_profile_images)
      }

      if (!this.uploadFile) return

      // append the files to FormData
      formData.append('uploadFile', this.uploadFile)
      formData.append('profileImages', this.profileImages)

      // should we ignore warnings
      if (ignoreWarnings) {
        formData.set('ignoreWarnings', 'true')
      }

      // save it
      this.save(formData)
    },
    // create user : operation = 1,update user : operation = 2,update profile images : operation = 3
    downloadExample (operation, e) {
      e.preventDefault()
      this.$http.get('user-import/example', { params: { role: this.role, operation } })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data], {type: 'text/csv'}))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `example-${this.role}-import.csv`)
          document.body.appendChild(link)
          link.click()
        })
        .catch(hr => {
          notify.error('There are issues with your import. Please review and try again')
        })
    }
  },
  mounted () {
    this.reset()
  }
}
</script>

<style lang="scss">
  .dashboard-block {
    .panel-grey {
      &.is-droppable {
        background-color: #bdbdbd;
      }
    }
  }
  .dropbox {
    outline: 2px dashed grey; /* the dash box */
    outline-offset: -10px;
    background: lightcyan;
    color: dimgray;
    padding: 2px 2px;
    min-height: 50px; /* minimum height */
    position: relative;
    cursor: pointer;
  }

  .input-file {
    opacity: 0; /* invisible but it's there! */
    width: 100%;
    height: 200px;
    position: absolute;
    cursor: pointer;
  }

  .dropbox:hover {
    background: lightblue; /* when mouse over to the drop zone, change color */
  }

  .dropbox p {
    font-size: 1.2em;
    text-align: center;
    padding: 10px 0;
  }

  .dropbox span {
    display: block;
    text-align: center;
    padding: 10px 0;
  }

</style>
